import React, {useRef, useState} from "react";
import {Badge, Button, Col, Form, Row} from "react-bootstrap";
import {RequireAuth} from "Components/Security/RequireAuth";
import {AdminCrudList} from "Views/Shared/Crud/AdminCrudList";
import {getTpvTransactions} from "Api/Query/TpvTransactionQuery";
import {MoneyAmount} from "Components/Money/MoneyAmount";
import {CenterSelect} from "Components/Center/CenterSelect";
import {PaymentStatus} from "Model/Payment/PaymentStatus";
import {PAYMENT_STATUS_COLORS} from "Model/Payment/PaymentStatusColors";
import {PAYMENT_STATUS_LABELS} from "Model/Payment/PaymentStatusLabels";
import {RefundConfirmationModal} from "Views/TpvTransactions/RefundConfirmationModal";

export const AdminTpvTransactionList: React.FC = () => {

    const page = useRef<number>(1)

    const [centerFilter, setCenterFilter] = useState<any>(null)
    const [customFiltersData, setCustomFiltersData] = useState<{ [key: string]: any }>({})
    const [customFiltersDataToApply, setCustomFiltersDataToApply] = useState<{ [key: string]: any }>(customFiltersData)
    const [refundTarget, setRefundTarget] = useState<any>(null)
    const [refreshSwitch, setRefreshSwitch] = useState<boolean>(false)

    const headers: string[] = [
        'FECHA',
        'IMPORTE',
        'ESTADO',
        'REFERENCIA / MÉTODO',
        'Nº INTENTO',
        'ERROR',
        'ID INSCRIPCIÓN',
        'ID ACTIVIDAD INSCRIPCIÓN',
        'ACCIONES',
    ]

    const applyFilterChanges = () => {
        let parsedFilters: any = {}

        if (customFiltersData.q) {
            parsedFilters = {...parsedFilters, q: customFiltersData.q}
        }
        if (customFiltersData.initDate) {
            parsedFilters = {...parsedFilters, initDate: customFiltersData.initDate}
        }
        if (customFiltersData.endDate) {
            parsedFilters = {...parsedFilters, endDate: customFiltersData.endDate}
        }
        if (customFiltersData.status) {
            parsedFilters = {...parsedFilters, status: customFiltersData.status}
        }
        if (customFiltersData.center) {
            parsedFilters = {...parsedFilters, center: customFiltersData.center}
        }
        if (customFiltersData.inscriptionId) {
            parsedFilters = {...parsedFilters, inscriptionId: customFiltersData.inscriptionId}
        }
        if (customFiltersData.inscriptionOrderActivityId) {
            parsedFilters = {...parsedFilters, inscriptionOrderActivityId: customFiltersData.inscriptionOrderActivityId}
        }
        if (customFiltersData.bankDsOrder) {
            parsedFilters = {...parsedFilters, bankDsOrder: customFiltersData.bankDsOrder}
        }

        page.current = 1
        parsedFilters = {...parsedFilters, page: page.current}
        setCustomFiltersDataToApply(parsedFilters)
    }

    const clearFilters = () => {
        page.current = 1
        setCenterFilter(null)
        setCustomFiltersData({})
        setCustomFiltersDataToApply({})
    }

    return (
        <RequireAuth>
            <AdminCrudList
                id="tpv-transactions-list"
                title={"Transacciones TPV"}
                headers={headers}
                pageSize={20}
                sort={"-createdAt"}
                queryMethod={getTpvTransactions}
                queryName={"adminListTpvTransactions"}
                deleteMethod={() => Promise.resolve()}
                canSelectMultipleRows={false}
                customFiltersData={customFiltersDataToApply}
                refreshSwitch={refreshSwitch}
                tableRow={(item: any, key: number) => {
                    const rawStatus = (item?.status ?? '').toLowerCase() as PaymentStatus
                    const statusLabel = PAYMENT_STATUS_LABELS[rawStatus] ?? rawStatus
                    const statusColor = PAYMENT_STATUS_COLORS[rawStatus] ?? 'secondary'

                    return (
                        <>
                            <td>{item.createdAt}</td>
                            <td><MoneyAmount amount={item.amount}/></td>
                            <td>
                                <Badge bg={statusColor}>{statusLabel}</Badge>
                            </td>
                            <td>
                                {item.bankDsOrder && <div><small className="text-muted">Ref:</small> {item.bankDsOrder}</div>}
                                {item.paymentMethodName && <div><small className="text-muted">Método:</small> {item.paymentMethodName}</div>}
                            </td>
                            <td className={"text-center"}>{item.attemptNumber}</td>
                            <td>{item.errorCode ?? '—'}</td>
                            <td>
                                {item.inscriptionId
                                    ? <a href={`/admin/inscriptions/${item.inscriptionId}`}><small>{item.inscriptionId}</small></a>
                                    : '—'
                                }
                            </td>
                            <td><small>{item.inscriptionOrderActivityId}</small></td>
                            <td className={"text-center align-middle"}>
                                {rawStatus === PaymentStatus.AUTHORIZED && (
                                    <Button
                                        variant={"danger"}
                                        size={"sm"}
                                        onClick={() => setRefundTarget(item)}
                                    >
                                        Realizar devolución
                                    </Button>
                                )}
                            </td>
                        </>
                    )
                }}
                customFilters={
                    <>
                        <Col xs={12} sm={6} lg={3}>
                            <Form.Group controlId={"tpvQueryFilter"}>
                                <Form.Label>Buscar</Form.Label>
                                <Form.Control
                                    type="text"
                                    onChange={(e: any) => {
                                        setCustomFiltersData({...customFiltersData, q: e.target.value ?? ''})
                                    }}
                                    value={customFiltersData.q ?? ''}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm={6} lg={3}>
                            <Form.Group controlId={"tpvInitDateFilter"}>
                                <Form.Label>Fecha desde</Form.Label>
                                <Form.Control
                                    type="date"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setCustomFiltersData({...customFiltersData, initDate: e.target.value})
                                    }}
                                    value={customFiltersData.initDate ?? ''}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm={6} lg={3}>
                            <Form.Group controlId={"tpvEndDateFilter"}>
                                <Form.Label>Fecha hasta</Form.Label>
                                <Form.Control
                                    type="date"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setCustomFiltersData({...customFiltersData, endDate: e.target.value})
                                    }}
                                    value={customFiltersData.endDate ?? ''}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm={6} lg={3}>
                            <Form.Group controlId={"tpvStatusFilter"}>
                                <Form.Label>Estado</Form.Label>
                                <Form.Select
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        const value = e.target.value
                                        if (value) {
                                            setCustomFiltersData({...customFiltersData, status: value})
                                        } else {
                                            const newFilters: any = {...customFiltersData}
                                            delete newFilters.status
                                            setCustomFiltersData(newFilters)
                                        }
                                    }}
                                    value={customFiltersData.status ?? ''}
                                >
                                    <option value="">Todos</option>
                                    <option value={PaymentStatus.PENDING}>{PAYMENT_STATUS_LABELS[PaymentStatus.PENDING]}</option>
                                    <option value={PaymentStatus.AUTHORIZED}>{PAYMENT_STATUS_LABELS[PaymentStatus.AUTHORIZED]}</option>
                                    <option value={PaymentStatus.FAILED}>{PAYMENT_STATUS_LABELS[PaymentStatus.FAILED]}</option>
                                    <option value={PaymentStatus.REFUNDED}>{PAYMENT_STATUS_LABELS[PaymentStatus.REFUNDED]}</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Row className={"mt-3 w-100"}>
                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group controlId={"tpvCenterFilter"}>
                                    <Form.Label>Centro</Form.Label>
                                    <CenterSelect
                                        value={centerFilter}
                                        onChange={(option: any) => {
                                            setCenterFilter(option)
                                            if (option && option.value) {
                                                setCustomFiltersData({...customFiltersData, center: option.value})
                                            } else {
                                                const newFilters: any = {...customFiltersData}
                                                delete newFilters.center
                                                setCustomFiltersData(newFilters)
                                            }
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group controlId={"tpvInscriptionIdFilter"}>
                                    <Form.Label>ID Inscripción</Form.Label>
                                    <Form.Control
                                        type="text"
                                        onChange={(e: any) => {
                                            setCustomFiltersData({...customFiltersData, inscriptionId: e.target.value ?? ''})
                                        }}
                                        value={customFiltersData.inscriptionId ?? ''}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group controlId={"tpvInscriptionOrderActivityIdFilter"}>
                                    <Form.Label>ID Actividad Inscripción</Form.Label>
                                    <Form.Control
                                        type="text"
                                        onChange={(e: any) => {
                                            setCustomFiltersData({...customFiltersData, inscriptionOrderActivityId: e.target.value ?? ''})
                                        }}
                                        value={customFiltersData.inscriptionOrderActivityId ?? ''}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group controlId={"tpvBankDsOrderFilter"}>
                                    <Form.Label>Referencia transacción TPV</Form.Label>
                                    <Form.Control
                                        type="text"
                                        onChange={(e: any) => {
                                            setCustomFiltersData({...customFiltersData, bankDsOrder: e.target.value ?? ''})
                                        }}
                                        value={customFiltersData.bankDsOrder ?? ''}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className={"mt-2"}>
                            <Col>
                                <Button
                                    className={"purple-btn"}
                                    onClick={applyFilterChanges}
                                >Buscar</Button>
                                &nbsp;
                                <Button
                                    variant={"secondary"}
                                    onClick={clearFilters}
                                >Limpiar filtros</Button>
                            </Col>
                        </Row>
                    </>
                }
            />

            <RefundConfirmationModal
                show={refundTarget !== null}
                transaction={refundTarget}
                onClose={() => setRefundTarget(null)}
                onRefunded={() => setRefreshSwitch(prev => !prev)}
            />
        </RequireAuth>
    )
}
